export type ManualInstallPlatform = 'ios' | 'macSafari'

// The `beforeinstallprompt` event is non-standard and deliberately excluded from lib.dom, but is
// what Chromium browsers fire to let us defer and trigger PWA installation from our own UI.
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

// Check whether the app is already running as an installed PWA
// If so, no install prompt should be shown.
export const getIsStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari lacks the display-mode media query; it exposes this non-standard flag instead
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true

const getIsIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ Safari reports a "Macintosh" UA by default; touch support tells it apart from real Macs
  (/macintosh/i.test(window.navigator.userAgent) && window.navigator.maxTouchPoints > 1)

const getIsMacSafari = () =>
  /macintosh/i.test(window.navigator.userAgent) &&
  /safari/i.test(window.navigator.userAgent) &&
  // other browsers also claim "Safari" in their UA, so real Safari is the absence of their tokens
  !/chrome|chromium|crios|edg|opr|fxios|firefox/i.test(window.navigator.userAgent) &&
  // an iPad masquerading as a Mac (see getIsIOS) needs the iOS instructions, not the Mac ones
  !getIsIOS()

// Returns which set of manual install instructions to show, or null when none are needed
export const getManualInstallPlatform = (isStandalone: boolean): ManualInstallPlatform | null => {
  if (isStandalone) return null
  if (getIsIOS()) return 'ios'
  if (getIsMacSafari()) return 'macSafari'
  return null
}
